import { compact, merge } from 'lodash/fp';
import { titleCase } from 'title-case';

import { JsonSchemaFilters } from 'dashboard/lib/workflows/objects';
import { snakify } from 'dashboard/lib/workflows/util';
import { WorkflowFieldObjectKey } from 'dashboard/models/workflow';
import { ObjectSchemaMap } from 'dashboard/redux/slices/object-schemas';
import { getFieldSchemaDescription } from 'lib/fields';
import {
  EMPTY_JSON_SCHEMA_OBJECT,
  isJsonSchemaRef,
  JsonSchema,
  JsonSchemaCore,
  JsonSchemaObject,
  JsonSchemaRef,
  JsonSchemaTypes,
} from 'lib/json-schema';
import { captureMessage } from 'lib/reporting';

export interface PlatformObjectJsonSchema extends JsonSchemaObject {
  properties: {
    objectType: {
      const: WorkflowFieldObjectKey;
    };
    template?: {
      const: string;
    };
  };
  required: string[];
}

export type PlatformJsonSchema = JsonSchemaCore | PlatformObjectJsonSchema;

export const MERGED_SCHEMA_ID = 'merged';

export const isPlatformObjectSchema = (schema: JsonSchema): schema is PlatformObjectJsonSchema => {
  return (
    'type' in schema && schema.type === JsonSchemaTypes.Object && 'objectType' in schema.properties
  );
};

export const getPlatformObjectTypeFromJsonSchema = (
  schema: JsonSchema,
): WorkflowFieldObjectKey | null => {
  return isPlatformObjectSchema(schema) ? schema.properties.objectType.const : null;
};

export const getPlatformTemplateFromJsonSchema = (schema: JsonSchema): string | null => {
  if (!isPlatformObjectSchema(schema)) {
    return null;
  }
  return schema.properties.template?.const || null;
};

export const createPlatformObjectJsonSchema = (
  objectType: WorkflowFieldObjectKey,
  template?: string,
): PlatformObjectJsonSchema => {
  const properties: PlatformObjectJsonSchema['properties'] = {
    objectType: {
      const: objectType,
    },
  };
  const required = ['objectType'];
  if (template) {
    properties['template'] = {
      const: template,
    };
    required.push('template');
  }
  return {
    type: JsonSchemaTypes.Object,
    properties,
    required,
  };
};

export const getTypeDisplayText = (schema: PlatformJsonSchema) => {
  return getPlatformObjectTypeFromJsonSchema(schema) || schema?.type;
};

export const generatePlatformObjectJsonSchema = ({
  platformObjectJsonSchema,
  workflowObjectKey,
  filters,
}: {
  platformObjectJsonSchema: ObjectSchemaMap;
  workflowObjectKey: WorkflowFieldObjectKey;
  filters?: JsonSchemaFilters;
}): JsonSchemaObject => {
  const schemaIds =
    filters?.['template-filters'] && filters['template-filters'].length > 0
      ? filters['template-filters'].map(({ id }) => getSchemaId(workflowObjectKey, id))
      : getSchemaId(workflowObjectKey, MERGED_SCHEMA_ID) in platformObjectJsonSchema
        ? [getSchemaId(workflowObjectKey, MERGED_SCHEMA_ID)]
        : [getSchemaId(workflowObjectKey)];
  const schemas = compact(schemaIds.map((id) => platformObjectJsonSchema[id]));
  if (schemas.length === 0) {
    captureMessage(`Platform object schema not found for ${schemaIds}`, 'warning');
    return EMPTY_JSON_SCHEMA_OBJECT;
  }

  // (evan.qi) deep merge multiple schemas to show as many properties as possible
  const oneSchema =
    schemas.length > 1
      ? mergePlatformObjectSchemas({
          description: `Type: ${titleCase(schemas[0].description ?? workflowObjectKey)}`,
          schemas,
        })
      : schemas[0];

  return {
    ...injectPropertyDescriptions(oneSchema),
    objectKey: workflowObjectKey,
  };
};

export const injectPropertyDescriptions = (schema: JsonSchemaObject): JsonSchemaObject => {
  return {
    ...schema,
    properties: Object.entries(schema.properties).reduce<JsonSchemaObject['properties']>(
      (acc, [propertyKey, propertySchema]) => ({
        ...acc,
        [propertyKey]: {
          ...propertySchema,
          description: isJsonSchemaRef(propertySchema)
            ? `Type: ${propertySchema.description}`
            : (propertySchema.description ?? getFieldSchemaDescription(propertyKey)),
        },
      }),
      {},
    ),
  };
};

export const resolveJssRef = (
  schema: JsonSchemaRef,
  platformObjectJsonSchema: ObjectSchemaMap = {},
): JsonSchemaObject => {
  return platformObjectJsonSchema[schema.$ref]
    ? injectPropertyDescriptions(platformObjectJsonSchema[schema.$ref])
    : EMPTY_JSON_SCHEMA_OBJECT;
};

// (evan.qi) lodash does not support deep merging in the same way Rails does.
// Instead of combining array options for enum/const/oneOf, we can only choose
// one template's enums.
const mergePlatformObjectSchemas = ({
  description,
  schemas,
}: {
  description: string;
  schemas: JsonSchemaObject[];
}): JsonSchemaObject => {
  return {
    type: JsonSchemaTypes.Object,
    description,
    properties: schemas
      .map((schema) => schema.properties)
      .reduce((acc, schemaProperties) => merge(acc, schemaProperties), {}),
  };
};

// (evan.qi) this is a hack to get the jsonapi name of the Persona object described by the schema
// returned from the workflows schema controller
export const getWorkflowObjectKeyFromSchemaRef = (schemaRef: string): WorkflowFieldObjectKey => {
  return schemaRef
    .split('/') // ['', 'schema', 'object-type', 'object-subtype', 'merged']
    .slice(2) // ['object-type', 'object-subtype', 'merged]
    .slice(0, -1) // ['object-type', 'object-subtype']
    .join('/') // 'object-type/object-subtype'
    .replace('_', '-') as WorkflowFieldObjectKey;
};

export const getSchemaId = (
  workflowObjectKey: WorkflowFieldObjectKey | string,
  identifier = '',
): string => {
  return ['/schema', snakify(workflowObjectKey), identifier].filter(Boolean).join('/');
};
